import { supabase } from "@/integrations/supabase/client";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { 
  format, 
  parseISO, 
  startOfMonth, 
  endOfMonth, 
  subMonths, 
  addMonths 
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { isDespesaOperacional } from '@/constants/categoriasFinanceiro';

function comEmpresa(query: any, empresaId?: string | null) {
  if (!empresaId) return query;
  return query.eq('empresa_id', empresaId);
}

// Re-exportar para uso no ContasRecorrentesService

export interface ContaPagar {
  id: string;
  descricao: string;
  valor: number | null;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'pendente' | 'pago' | 'vencido' | 'deletada';
  categoria: string;
  fornecedor?: string;
  contrato_id?: string;
  /** Tipo do contrato vinculado: fotovoltaico ou carregador. Quando null/ausente, assume fotovoltaico. */
  contrato_tipo?: TipoContratoFinanceiro | null;
  parceiro_id?: string;
  observacoes?: string;
  tipo_pagamento?: 'avista' | 'parcelado' | 'recorrente';
  forma_pagamento?: string;
  quantidade_parcelas?: number;
  numero_parcela?: number;
  id_parcelamento?: string;
  /** Modalidade de parcelamento: mesmo_dia = mesmo dia do mês; 28_dias = parcela a cada 28 dias. Null = mesmo_dia (retrocompatível) */
  modalidade_parcelamento?: 'mesmo_dia' | '28_dias' | null;
  recorrente_id?: string;
  frequencia?: 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual';
  comprovante_url?: string;
  comprovante_nome_arquivo?: string;
  boleto_url?: string;
  boleto_nome_arquivo?: string;
  boletos?: { id: string; url: string; nome_arquivo: string; created_at?: string }[];
  nota_fiscal_url?: string;
  nota_fiscal_nome_arquivo?: string;
  notas_fiscais?: { id: string; url: string; nome_arquivo: string; created_at?: string }[];
  doc_excecao_boleto?: boolean;
  doc_excecao_nota_fiscal?: boolean;
  doc_excecao_motivo?: string | null;
  doc_excecao_por?: string | null;
  doc_excecao_em?: string | null;
  empresa_id?: string | null;
  created_at: string;
}

export interface ContaReceber {
  id: string;
  descricao: string;
  valor: number;
  data_vencimento: string;
  data_recebimento?: string;
  status: 'pendente' | 'recebido' | 'vencido';
  categoria: string;
  cliente?: string;
  contrato_id?: string;
  /** Etapa do pagamento previsto (assinatura, inicio, conclusao, ligacao). Identificação do vínculo é somente por contrato_id + contrato_etapa. */
  contrato_etapa?: string | null;
  /** Tipo do contrato vinculado: fotovoltaico ou carregador. Quando null/ausente, assume fotovoltaico. */
  contrato_tipo?: TipoContratoFinanceiro | null;
  observacoes?: string;
  tipo_pagamento?: 'avista' | 'parcelado' | 'recorrente';
  forma_pagamento?: string;
  quantidade_parcelas?: number;
  numero_parcela?: number;
  id_parcelamento?: string;
  recorrente_id?: string;
  frequencia?: 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual';
  comprovante_url?: string;
  comprovante_nome_arquivo?: string;
  boleto_url?: string;
  boleto_nome_arquivo?: string;
  boletos?: { id: string; url: string; nome_arquivo: string; created_at?: string }[];
  nota_fiscal_url?: string;
  nota_fiscal_nome_arquivo?: string;
  notas_fiscais?: { id: string; url: string; nome_arquivo: string; created_at?: string }[];
  doc_excecao_boleto?: boolean;
  doc_excecao_nota_fiscal?: boolean;
  doc_excecao_motivo?: string | null;
  doc_excecao_por?: string | null;
  doc_excecao_em?: string | null;
  empresa_id?: string | null;
  created_at: string;
}

export interface RecebimentoPrevisto {
  etapa: string;
  descricao: string;
  valor: number;
  percentual: number;
  forma_pagamento: string;
  numero_parcelas: number;
  valor_parcela: number;
}

export type TipoContratoFinanceiro = 'fotovoltaico' | 'carregador';

export interface ContratoAberto {
  id: string;
  lead_id: string;
  cliente_nome: string;
  valor_total: number;
  valor_recebido: number;
  valor_pendente: number;
  parcelas_pagas: number;
  parcelas_total: number;
  proximo_vencimento?: string;
  status: string;
  created_at?: string;
  /** Tipo do contrato: fotovoltaico ou carregador (carregadores veiculares) */
  tipo_contrato?: TipoContratoFinanceiro;
  // Campos de modalidade de pagamento
  integracao_modalidade_pagamento?: number;
  integracao_valor_total?: number;
  integracao_percentual_assinatura?: number;
  integracao_percentual_inicio_obra?: number;
  integracao_percentual_conclusao_obra?: number;
  integracao_percentual_ligacao_definitiva?: number;
  integracao_valor_assinatura?: number;
  integracao_valor_inicio_obra?: number;
  integracao_valor_conclusao_obra?: number;
  integracao_valor_ligacao_definitiva?: number;
  integracao_forma_pagamento_assinatura?: string;
  integracao_forma_pagamento_inicio_obra?: string;
  integracao_forma_pagamento_conclusao_obra?: string;
  integracao_forma_pagamento_ligacao_definitiva?: string;
  integracao_numero_parcelas_assinatura?: number;
  integracao_numero_parcelas_inicio_obra?: number;
  integracao_numero_parcelas_conclusao_obra?: number;
  integracao_numero_parcelas_ligacao_definitiva?: number;
  integracao_valor_parcela_assinatura?: number;
  integracao_valor_parcela_inicio_obra?: number;
  integracao_valor_parcela_conclusao_obra?: number;
  integracao_valor_parcela_ligacao_definitiva?: number;
}

export interface CustoObra {
  id: string;
  contrato_id: string;
  /** Tipo do contrato vinculado: fotovoltaico ou carregador */
  contrato_tipo?: TipoContratoFinanceiro | null;
  descricao: string;
  valor: number;
  categoria: string;
  data: string;
  fornecedor?: string;
  observacoes?: string;
  created_at: string;
}

export interface ResumoFinanceiro {
  receitas_mes: number;
  despesas_mes: number;
  saldo_mes: number;
  receitas_total: number;
  despesas_total: number;
  saldo_total: number;
  contas_pagar_pendentes: number;
  contas_receber_pendentes: number;
  valor_contratos_aberto: number;
  total_volume_contratos_aberto: number;
  valor_recebido_contratos_aberto: number;
  custos_obras_mes: number;
}

export interface DadosGrafico {
  mes: string;
  receitas: number;
  despesas: number;
  saldo: number;
}

class FinanceiroService {
  // Contas a Pagar
  async listarContasPagar(filtros?: {
    status?: string;
    data_inicio?: string;
    data_fim?: string;
    contrato_id?: string;
    empresa_id?: string | null;
  }): Promise<ContaPagar[]> {
    let query: any = supabase
      .from('contas_pagar')
      .select('*')
      .neq('status', 'deletada') // Não incluir contas deletadas
      .order('data_vencimento', { ascending: true });

    if (filtros?.status && filtros.status !== 'deletada') {
      query = query.eq('status', filtros.status);
    }

    if (filtros?.data_inicio) {
      query = query.gte('data_vencimento', filtros.data_inicio);
    }

    if (filtros?.data_fim) {
      query = query.lte('data_vencimento', filtros.data_fim);
    }

    if (filtros?.contrato_id) {
      query = query.eq('contrato_id', filtros.contrato_id);
    }

    query = comEmpresa(query, filtros?.empresa_id);

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao listar contas a pagar:', error);
      throw error;
    }

    return (data || []) as ContaPagar[];
  }

  async criarContaPagar(conta: Omit<ContaPagar, 'id' | 'created_at'>): Promise<ContaPagar> {
    // Constraint no banco: tipo_pagamento IS NULL OR tipo_pagamento IN ('avista', 'parcelado', 'recorrente')
    const tiposValidos: string[] = ['avista', 'parcelado', 'recorrente'];
    const v = conta.tipo_pagamento;
    const tipoPagamentoSanitizado =
      v && typeof v === 'string' && tiposValidos.includes(v) ? v : null;

    // Limpar campos opcionais vazios
    const contaLimpa: any = {
      descricao: conta.descricao,
      valor: conta.valor !== null && conta.valor !== undefined ? conta.valor : null,
      data_vencimento: conta.data_vencimento,
      categoria: conta.categoria,
      status: conta.status,
      fornecedor: conta.fornecedor && String(conta.fornecedor).trim() !== '' ? conta.fornecedor : null,
      contrato_id: conta.contrato_id && String(conta.contrato_id).trim() !== '' ? conta.contrato_id : null,
      contrato_tipo: conta.contrato_tipo && String(conta.contrato_tipo).trim() !== '' ? conta.contrato_tipo : null,
      parceiro_id: conta.parceiro_id && String(conta.parceiro_id).trim() !== '' ? conta.parceiro_id : null,
      observacoes: conta.observacoes && String(conta.observacoes).trim() !== '' ? conta.observacoes : null,
      data_pagamento: conta.data_pagamento || null,
      tipo_pagamento: tipoPagamentoSanitizado,
      forma_pagamento: conta.forma_pagamento && String(conta.forma_pagamento).trim() !== '' ? conta.forma_pagamento : null,
      quantidade_parcelas: conta.quantidade_parcelas || null,
      numero_parcela: conta.numero_parcela || null,
      id_parcelamento: conta.id_parcelamento || null,
      modalidade_parcelamento: conta.modalidade_parcelamento && String(conta.modalidade_parcelamento).trim() !== '' ? conta.modalidade_parcelamento : null,
      frequencia: conta.frequencia || null,
      empresa_id: conta.empresa_id || null,
    };

    const { data, error } = await supabase
      .from('contas_pagar')
      .insert([contaLimpa])
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar conta a pagar:', error);
      throw error;
    }

    return data as ContaPagar;
  }

  async atualizarContaPagar(id: string, updates: Partial<ContaPagar>): Promise<ContaPagar> {
    // Limpar campos opcionais vazios
    const updatesLimpos: any = { ...updates };
    if ('fornecedor' in updatesLimpos) {
      updatesLimpos.fornecedor = updatesLimpos.fornecedor && updatesLimpos.fornecedor.trim() !== '' ? updatesLimpos.fornecedor : null;
    }
    if ('contrato_id' in updatesLimpos) {
      updatesLimpos.contrato_id = updatesLimpos.contrato_id && updatesLimpos.contrato_id.trim() !== '' ? updatesLimpos.contrato_id : null;
    }
    if ('contrato_tipo' in updatesLimpos) {
      updatesLimpos.contrato_tipo = updatesLimpos.contrato_tipo && String(updatesLimpos.contrato_tipo).trim() !== '' ? updatesLimpos.contrato_tipo : null;
    }
    if ('parceiro_id' in updatesLimpos) {
      updatesLimpos.parceiro_id = updatesLimpos.parceiro_id && updatesLimpos.parceiro_id.trim() !== '' ? updatesLimpos.parceiro_id : null;
    }
    if ('observacoes' in updatesLimpos) {
      updatesLimpos.observacoes = updatesLimpos.observacoes && updatesLimpos.observacoes.trim() !== '' ? updatesLimpos.observacoes : null;
    }
    // Constraint no banco: tipo_pagamento IS NULL OR tipo_pagamento IN ('avista', 'parcelado', 'recorrente')
    const tiposValidos = ['avista', 'parcelado', 'recorrente'];
    if ('tipo_pagamento' in updatesLimpos) {
      const v = updatesLimpos.tipo_pagamento;
      updatesLimpos.tipo_pagamento =
        v && typeof v === 'string' && tiposValidos.includes(v) ? v : null;
    }

    const { data, error } = await supabase
      .from('contas_pagar')
      .update(updatesLimpos)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar conta a pagar:', error);
      throw error;
    }

    return data as ContaPagar;
  }

  async deletarContaPagar(id: string): Promise<void> {
    const { error } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar conta a pagar:', error);
      throw error;
    }
  }

  async deletarTodasParcelas(idParcelamento: string): Promise<void> {
    const { error } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('id_parcelamento', idParcelamento);

    if (error) {
      console.error('Erro ao deletar todas as parcelas:', error);
      throw error;
    }
  }

  async deletarParcelasFuturas(idParcelamento: string, numeroParcelaAtual: number): Promise<void> {
    // Deletar a parcela atual e todas as parcelas com numero_parcela >= numeroParcelaAtual
    const { error } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('id_parcelamento', idParcelamento)
      .gte('numero_parcela', numeroParcelaAtual);

    if (error) {
      console.error('Erro ao deletar parcelas futuras:', error);
      throw error;
    }
  }

  async listarParcelas(idParcelamento: string): Promise<ContaPagar[]> {
    const { data, error } = await supabase
      .from('contas_pagar')
      .select('*')
      .eq('id_parcelamento', idParcelamento)
      .order('numero_parcela', { ascending: true });

    if (error) {
      console.error('Erro ao listar parcelas:', error);
      throw error;
    }

    return (data || []) as ContaPagar[];
  }

  async atualizarParcelas(
    idParcelamento: string,
    updates: Partial<ContaPagar>,
    escopo: 'apenas' | 'futuras' | 'todas',
    numeroParcelaAtual?: number
  ): Promise<void> {
    let query = supabase
      .from('contas_pagar')
      .update(updates)
      .eq('id_parcelamento', idParcelamento);

    if (escopo === 'futuras' && numeroParcelaAtual !== undefined) {
      query = query.gte('numero_parcela', numeroParcelaAtual);
    } else if (escopo === 'apenas' && numeroParcelaAtual !== undefined) {
      query = query.eq('numero_parcela', numeroParcelaAtual);
    }
    // Se escopo === 'todas', não adiciona filtro adicional

    const { error } = await query;

    if (error) {
      console.error('Erro ao atualizar parcelas:', error);
      throw error;
    }
  }

  async listarRecorrentes(recorrenteId: string): Promise<ContaPagar[]> {
    const { data, error } = await supabase
      .from('contas_pagar')
      .select('*')
      .eq('recorrente_id', recorrenteId)
      .order('data_vencimento', { ascending: true });

    if (error) {
      console.error('Erro ao listar transações recorrentes:', error);
      throw error;
    }

    return (data || []) as ContaPagar[];
  }

  async atualizarRecorrentes(
    recorrenteId: string,
    updates: Partial<ContaPagar>,
    escopo: 'apenas' | 'futuras' | 'todas',
    dataVencimentoAtual?: string
  ): Promise<void> {
    let query = supabase
      .from('contas_pagar')
      .update(updates)
      .eq('recorrente_id', recorrenteId);

    if (escopo === 'futuras' && dataVencimentoAtual) {
      query = query.gte('data_vencimento', dataVencimentoAtual);
    } else if (escopo === 'apenas' && dataVencimentoAtual) {
      query = query.eq('data_vencimento', dataVencimentoAtual);
    }
    // Se escopo === 'todas', não adiciona filtro adicional

    const { error } = await query;

    if (error) {
      console.error('Erro ao atualizar transações recorrentes:', error);
      throw error;
    }
  }

  async deletarTodasRecorrentes(recorrenteId: string): Promise<void> {
    // Deletar todas as transações
    const { error: errorDelete } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('recorrente_id', recorrenteId);

    if (errorDelete) {
      console.error('Erro ao deletar todas as transações recorrentes:', errorDelete);
      throw errorDelete;
    }

    // Desativar a recorrência para evitar que novas contas sejam geradas
    const { error: errorUpdate } = await supabase
      .from('contas_pagar_recorrentes')
      .update({ ativo: false })
      .eq('id', recorrenteId);

    if (errorUpdate) {
      console.error('Erro ao desativar recorrência:', errorUpdate);
      throw errorUpdate;
    }
  }

  async deletarRecorrentesFuturas(recorrenteId: string, dataVencimentoAtual: string): Promise<void> {
    // Buscar a data da última transação antes da data atual para atualizar data_fim
    const { data: contasAnteriores } = await supabase
      .from('contas_pagar')
      .select('data_vencimento')
      .eq('recorrente_id', recorrenteId)
      .lt('data_vencimento', dataVencimentoAtual)
      .order('data_vencimento', { ascending: false })
      .limit(1);

    // Deletar a transação atual e todas as transações futuras (data_vencimento >= dataVencimentoAtual)
    const { error: errorDelete } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('recorrente_id', recorrenteId)
      .gte('data_vencimento', dataVencimentoAtual);

    if (errorDelete) {
      console.error('Erro ao deletar transações recorrentes futuras:', errorDelete);
      throw errorDelete;
    }

    // Atualizar data_fim da recorrência para a última data antes da exclusão
    if (contasAnteriores && contasAnteriores.length > 0) {
      const ultimaData = contasAnteriores[0].data_vencimento;
      const { error: errorUpdate } = await supabase
        .from('contas_pagar_recorrentes')
        .update({ data_fim: ultimaData })
        .eq('id', recorrenteId);

      if (errorUpdate) {
        console.error('Erro ao atualizar data_fim da recorrência:', errorUpdate);
        throw errorUpdate;
      }
    } else {
      // Se não há transações anteriores, desativar a recorrência
      const { error: errorUpdate } = await supabase
        .from('contas_pagar_recorrentes')
        .update({ ativo: false })
        .eq('id', recorrenteId);

      if (errorUpdate) {
        console.error('Erro ao desativar recorrência:', errorUpdate);
        throw errorUpdate;
      }
    }
  }

  async deletarContaRecorrenteIndividual(contaId: string, recorrenteId: string, dataVencimento: string): Promise<void> {
    // Deletar a transação
    const { error: errorDelete } = await supabase
      .from('contas_pagar')
      .delete()
      .eq('id', contaId);

    if (errorDelete) {
      console.error('Erro ao deletar conta recorrente individual:', errorDelete);
      throw errorDelete;
    }

    // Registrar a exclusão na tabela de exclusões para evitar recriação
    const { error: errorInsert } = await (supabase.from('contas_pagar_recorrentes_excluidas' as any) as any)
      .insert({
        recorrente_id: recorrenteId,
        data_vencimento: dataVencimento
      });

    if (errorInsert) {
      console.error('Erro ao registrar exclusão de conta recorrente:', errorInsert);
      // Não lançar erro aqui, pois a transação já foi deletada
      // Apenas logar o erro para não interromper o fluxo
    }
  }

  // Contas a Receber
  async listarContasReceber(filtros?: {
    status?: string;
    data_inicio?: string;
    data_fim?: string;
    empresa_id?: string | null;
  }): Promise<ContaReceber[]> {
    let query = (supabase as any)
      .from('contas_receber')
      .select('*')
      .order('data_vencimento', { ascending: true });

    if (filtros?.status) {
      query = query.eq('status', filtros.status);
    }

    if (filtros?.data_inicio) {
      query = query.gte('data_vencimento', filtros.data_inicio);
    }

    if (filtros?.data_fim) {
      query = query.lte('data_vencimento', filtros.data_fim);
    }

    query = comEmpresa(query, filtros?.empresa_id);

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao listar contas a receber:', error);
      throw error;
    }

    return (data || []) as ContaReceber[];
  }

  async criarContaReceber(conta: Omit<ContaReceber, 'id' | 'created_at'>): Promise<ContaReceber> {
    // Limpar campos opcionais vazios
    // Nota: valor é obrigatório em contas_receber (NOT NULL)
    const contaLimpa: any = {
      descricao: conta.descricao,
      valor: conta.valor, // Valor é obrigatório, não pode ser null
      data_vencimento: conta.data_vencimento,
      categoria: conta.categoria,
      status: conta.status,
    };

    // Adicionar campos opcionais apenas se tiverem valor
    if (conta.cliente && String(conta.cliente).trim() !== '') {
      contaLimpa.cliente = conta.cliente;
    }
    if (conta.contrato_id && String(conta.contrato_id).trim() !== '') {
      contaLimpa.contrato_id = conta.contrato_id;
    }
    if (conta.contrato_tipo && String(conta.contrato_tipo).trim() !== '') {
      contaLimpa.contrato_tipo = conta.contrato_tipo;
    }
    if (conta.contrato_etapa && String(conta.contrato_etapa).trim() !== '') {
      contaLimpa.contrato_etapa = conta.contrato_etapa;
    }
    if (conta.observacoes && String(conta.observacoes).trim() !== '') {
      contaLimpa.observacoes = conta.observacoes;
    }
    if (conta.data_recebimento) {
      contaLimpa.data_recebimento = conta.data_recebimento;
    }
    if (conta.tipo_pagamento) {
      contaLimpa.tipo_pagamento = conta.tipo_pagamento;
    }
    if (conta.forma_pagamento && String(conta.forma_pagamento).trim() !== '') {
      contaLimpa.forma_pagamento = conta.forma_pagamento;
    }
    if (conta.quantidade_parcelas !== null && conta.quantidade_parcelas !== undefined) {
      contaLimpa.quantidade_parcelas = conta.quantidade_parcelas;
    }
    if (conta.numero_parcela !== null && conta.numero_parcela !== undefined) {
      contaLimpa.numero_parcela = conta.numero_parcela;
    }
    if (conta.id_parcelamento && String(conta.id_parcelamento).trim() !== '') {
      contaLimpa.id_parcelamento = conta.id_parcelamento;
    }
    if (conta.frequencia) {
      contaLimpa.frequencia = conta.frequencia;
    }
    if (conta.empresa_id) {
      contaLimpa.empresa_id = conta.empresa_id;
    }

    const { data, error } = await (supabase as any)
      .from('contas_receber')
      .insert([contaLimpa])
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar conta a receber:', error);
      console.error('Dados sendo enviados:', JSON.stringify(contaLimpa, null, 2));
      console.error('Código do erro:', error.code);
      console.error('Mensagem do erro:', error.message);
      console.error('Detalhes do erro:', error.details);
      console.error('Hint do erro:', error.hint);
      
      // Verificar se o erro é relacionado a campos que não existem (migration não aplicada)
      if (error.code === '42703' || (error.message?.includes('column') && error.message?.includes('does not exist'))) {
        const errorMessage = 'Erro: Campos de parcelas não encontrados na tabela. Por favor, aplique a migration 20250206_add_parcelas_to_contas_receber.sql no banco de dados.';
        console.error(errorMessage);
        throw new Error(errorMessage);
      }
      
      // Se não for erro de campo não encontrado, lançar o erro original com mais detalhes
      const errorMessage = error.message || error.details || error.hint || 'Erro desconhecido ao criar conta a receber';
      const fullErrorMessage = `Erro ao criar conta a receber: ${errorMessage}${error.code ? ` (Código: ${error.code})` : ''}`;
      console.error('Erro completo:', fullErrorMessage);
      throw new Error(fullErrorMessage);
    }

    return data as ContaReceber;
  }

  async atualizarContaReceber(id: string, updates: Partial<ContaReceber>): Promise<ContaReceber> {
    // Limpar campos opcionais vazios
    const updatesLimpos: any = { ...updates };
    if ('cliente' in updatesLimpos) {
      updatesLimpos.cliente = updatesLimpos.cliente && updatesLimpos.cliente.trim() !== '' ? updatesLimpos.cliente : null;
    }
    if ('contrato_id' in updatesLimpos) {
      updatesLimpos.contrato_id = updatesLimpos.contrato_id && updatesLimpos.contrato_id.trim() !== '' ? updatesLimpos.contrato_id : null;
    }
    if ('contrato_tipo' in updatesLimpos) {
      updatesLimpos.contrato_tipo = updatesLimpos.contrato_tipo && String(updatesLimpos.contrato_tipo).trim() !== '' ? updatesLimpos.contrato_tipo : null;
    }
    if ('contrato_etapa' in updatesLimpos) {
      updatesLimpos.contrato_etapa = updatesLimpos.contrato_etapa && String(updatesLimpos.contrato_etapa).trim() !== '' ? updatesLimpos.contrato_etapa : null;
    }
    if ('observacoes' in updatesLimpos) {
      updatesLimpos.observacoes = updatesLimpos.observacoes && updatesLimpos.observacoes.trim() !== '' ? updatesLimpos.observacoes : null;
    }

    const { data, error } = await (supabase as any)
      .from('contas_receber')
      .update(updatesLimpos)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar conta a receber:', error);
      throw error;
    }

    return data as ContaReceber;
  }

  async deletarContaReceber(id: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('contas_receber')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar conta a receber:', error);
      throw error;
    }
  }

  async deletarTodasParcelasReceber(idParcelamento: string): Promise<void> {
    const { error } = await (supabase as any)
      .from('contas_receber')
      .delete()
      .eq('id_parcelamento', idParcelamento);

    if (error) {
      console.error('Erro ao deletar todas as parcelas a receber:', error);
      throw error;
    }
  }

  async deletarParcelasFuturasReceber(idParcelamento: string, numeroParcelaAtual: number): Promise<void> {
    // Deletar a parcela atual e todas as parcelas com numero_parcela >= numeroParcelaAtual
    const { error } = await (supabase as any)
      .from('contas_receber')
      .delete()
      .eq('id_parcelamento', idParcelamento)
      .gte('numero_parcela', numeroParcelaAtual);

    if (error) {
      console.error('Erro ao deletar parcelas futuras a receber:', error);
      throw error;
    }
  }

  async listarParcelasReceber(idParcelamento: string): Promise<ContaReceber[]> {
    const { data, error } = await (supabase as any)
      .from('contas_receber')
      .select('*')
      .eq('id_parcelamento', idParcelamento)
      .order('numero_parcela', { ascending: true });

    if (error) {
      console.error('Erro ao listar parcelas a receber:', error);
      throw error;
    }

    return (data || []) as ContaReceber[];
  }

  async atualizarParcelasReceber(
    idParcelamento: string,
    updates: Partial<ContaReceber>,
    escopo: 'apenas' | 'futuras' | 'todas',
    numeroParcelaAtual?: number
  ): Promise<void> {
    const updatesLimpos: any = { ...updates };
    
    let query: any = (supabase as any)
      .from('contas_receber')
      .update(updatesLimpos)
      .eq('id_parcelamento', idParcelamento);

    if (escopo === 'futuras' && numeroParcelaAtual !== undefined) {
      query = query.gte('numero_parcela', numeroParcelaAtual);
    } else if (escopo === 'apenas' && numeroParcelaAtual !== undefined) {
      query = query.eq('numero_parcela', numeroParcelaAtual);
    }
    // Se escopo === 'todas', não adiciona filtro adicional

    const { error } = await query;

    if (error) {
      console.error('Erro ao atualizar parcelas a receber:', error);
      throw error;
    }
  }

  // Contratos em Aberto
  async listarContratosAberto(): Promise<ContratoAberto[]> {
    const { data, error } = await supabase
      .rpc('get_contratos_aberto_financeiro');

    if (error) {
      console.error('Erro ao listar contratos em aberto:', error);
      // Se a função RPC não existir, buscar diretamente dos contratos
      return this.listarContratosAbertoFallback();
    }

    return data || [];
  }

  async finalizarContrato(contratoId: string): Promise<void> {
    try {
      console.log('🔍 [FINANCEIRO] Iniciando finalização do contrato:', contratoId);
      
      // Usar fetchWithAuth para respeitar políticas RBAC
      const response = await fetchWithAuth(`/api/contratos/${contratoId}/finalizar`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });

      console.log('📡 [FINANCEIRO] Resposta recebida:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        console.error('❌ [FINANCEIRO] Erro na resposta:', errorData);
        const errorMessage = errorData.details 
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || `Erro ao finalizar contrato: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('✅ [FINANCEIRO] Contrato finalizado com sucesso:', data);
    } catch (error) {
      console.error('❌ [FINANCEIRO] Erro ao finalizar contrato:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro desconhecido ao finalizar contrato');
    }
  }

  private async listarContratosAbertoFallback(): Promise<ContratoAberto[]> {
    const [sun7Result, carregadoresResult] = await Promise.all([
      supabase
        .from('contratos_sun7')
        .select(`
          id,
          lead_id,
          valor_total,
          integracao_valor_total,
          valor_integracao,
          status,
          created_at,
          leads:lead_id ( nome )
        `)
        .in('status', ['proposta_aceita', 'contrato_gerado', 'assinado', 'em_andamento']),
      (async () => {
        const { data: propostas } = await (supabase.from('propostas_carregadores' as any) as any)
          .select('id, lead_id, valor_total, forma_pagamento')
          .eq('status', 'aceita');
        if (!propostas?.length) return [];
        const propostasArray = propostas as any[];
        const { data: contratos } = await (supabase.from('contratos_carregadores' as any) as any)
          .select('id, lead_id, proposta_id, status, created_at')
          .in('proposta_id', propostasArray.map((p: any) => p.id))
          .not('contrato_pdf_url', 'is', null);
        if (!contratos?.length) return [];
        const contratosArray = contratos as any[];
        const leadIds = [...new Set(contratosArray.map((c: any) => c.lead_id))] as string[];
        const { data: leads } = await supabase.from('leads').select('id, nome, razao_social').in('id', leadIds);
        const leadsMap = new Map((leads || []).map((l: any) => [l.id, l]));
        const propostasMap = new Map(propostasArray.map((p: any) => [p.id, p]));
        return contratosArray.map((cc: any) => {
          const p = propostasMap.get(cc.proposta_id) as any;
          const l = leadsMap.get(cc.lead_id);
          const valorTotal = p?.valor_total ?? 0;
          return {
            id: cc.id,
            lead_id: cc.lead_id,
            cliente_nome: l?.nome || l?.razao_social || 'Cliente',
            valor_total: valorTotal,
            valor_recebido: 0,
            valor_pendente: valorTotal,
            parcelas_pagas: 0,
            parcelas_total: 1,
            status: cc.status || 'assinado',
            created_at: cc.created_at,
            tipo_contrato: 'carregador' as const,
            integracao_modalidade_pagamento: 1,
            integracao_valor_total: valorTotal,
          };
        });
      })(),
    ]);

    if (sun7Result.error) {
      console.error('Erro ao listar contratos sun7:', sun7Result.error);
    }

    const sun7List = (sun7Result.data || []).map((c: any) => {
      const valorIntegracao = c.integracao_valor_total ?? c.valor_integracao ?? 0;
      return {
        id: c.id,
        lead_id: c.lead_id,
        cliente_nome: c.leads?.nome || 'Cliente',
        valor_total: c.valor_total ?? 0,
        valor_recebido: 0,
        valor_pendente: valorIntegracao,
        parcelas_pagas: 0,
        parcelas_total: 1,
        status: c.status,
        created_at: c.created_at,
        tipo_contrato: 'fotovoltaico' as const,
        integracao_modalidade_pagamento: 1,
        integracao_valor_total: valorIntegracao,
      };
    });

    return [...sun7List, ...(Array.isArray(carregadoresResult) ? carregadoresResult : [])];
  }

  // Custos de Obras
  async listarCustosObras(filtros?: {
    contrato_id?: string;
    data_inicio?: string;
    data_fim?: string;
  }): Promise<CustoObra[]> {
    let query = supabase
      .from('custos_obras')
      .select('*')
      .order('data', { ascending: false });

    if (filtros?.contrato_id) {
      query = query.eq('contrato_id', filtros.contrato_id);
    }

    if (filtros?.data_inicio) {
      query = query.gte('data', filtros.data_inicio);
    }

    if (filtros?.data_fim) {
      query = query.lte('data', filtros.data_fim);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erro ao listar custos de obras:', error);
      throw error;
    }

    return data || [];
  }

  async criarCustoObra(custo: Omit<CustoObra, 'id' | 'created_at'>): Promise<CustoObra> {
    // Limpar campos opcionais vazios
    const custoLimpo: any = {
      contrato_id: custo.contrato_id,
      descricao: custo.descricao,
      valor: custo.valor,
      categoria: custo.categoria,
      data: custo.data,
      fornecedor: custo.fornecedor && String(custo.fornecedor).trim() !== '' ? custo.fornecedor : null,
      observacoes: custo.observacoes && String(custo.observacoes).trim() !== '' ? custo.observacoes : null,
    };
    if (custo.contrato_tipo && String(custo.contrato_tipo).trim() !== '') {
      custoLimpo.contrato_tipo = custo.contrato_tipo;
    }

    const { data, error } = await supabase
      .from('custos_obras')
      .insert([custoLimpo])
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar custo de obra:', error);
      throw error;
    }

    return data;
  }

  async atualizarCustoObra(id: string, updates: Partial<CustoObra>): Promise<CustoObra> {
    // Limpar campos opcionais vazios
    const updatesLimpos: any = { ...updates };
    if ('fornecedor' in updatesLimpos) {
      updatesLimpos.fornecedor = updatesLimpos.fornecedor && updatesLimpos.fornecedor.trim() !== '' ? updatesLimpos.fornecedor : null;
    }
    if ('observacoes' in updatesLimpos) {
      updatesLimpos.observacoes = updatesLimpos.observacoes && updatesLimpos.observacoes.trim() !== '' ? updatesLimpos.observacoes : null;
    }

    const { data, error } = await supabase
      .from('custos_obras')
      .update(updatesLimpos)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar custo de obra:', error);
      throw error;
    }

    return data;
  }

  async deletarCustoObra(id: string): Promise<void> {
    const { error } = await supabase
      .from('custos_obras')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao deletar custo de obra:', error);
      throw error;
    }
  }

  // Resumo Financeiro
  async obterResumoFinanceiro(data_inicio?: string, data_fim?: string, empresaId?: string | null): Promise<ResumoFinanceiro> {
    // Buscar dados de diferentes fontes
    const hoje = new Date();
    const inicioMes = data_inicio || new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
    const fimMes = data_fim || new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];

    // Contas a receber do mês (por vencimento para bater com as abas)
    const { data: receitasMes } = await comEmpresa(
      (supabase as any)
        .from('contas_receber')
        .select('valor, status')
        .gte('data_vencimento', inicioMes)
        .lte('data_vencimento', fimMes),
      empresaId
    );

    // Contas a pagar do mês (por vencimento para bater com as abas)
    const { data: despesasMes } = await comEmpresa(
      (supabase as any)
        .from('contas_pagar')
        .select('valor, status, categoria')
        .gte('data_vencimento', inicioMes)
        .lte('data_vencimento', fimMes)
        .neq('status', 'deletada'),
      empresaId
    );

    // Contas pendentes
    const { data: contasPagarPendentes } = await comEmpresa(
      (supabase as any)
        .from('contas_pagar')
        .select('valor')
        .neq('status', 'pago')
        .neq('status', 'deletada'),
      empresaId
    );

    const { data: contasReceberPendentes } = await comEmpresa(
      (supabase as any)
        .from('contas_receber')
        .select('valor')
        .neq('status', 'recebido'),
      empresaId
    );

    // Totais
    const { data: receitasTotal } = await comEmpresa(
      (supabase as any)
        .from('contas_receber')
        .select('valor')
        .eq('status', 'recebido'),
      empresaId
    );

    const { data: despesasTotal } = await comEmpresa(
      (supabase as any)
        .from('contas_pagar')
        .select('valor, categoria')
        .eq('status', 'pago'),
      empresaId
    );

    // Custos de obras do mês
    const { data: custosObrasMes } = await comEmpresa(
      (supabase as any)
        .from('custos_obras')
        .select('valor')
        .gte('data', inicioMes)
        .lte('data', fimMes),
      empresaId
    );

    // Contratos em aberto
    const contratosAberto = await this.listarContratosAberto();
    const valorContratosAberto = contratosAberto.reduce((acc, c) => acc + (c.valor_pendente || 0), 0);
    const totalVolumeContratosAberto = contratosAberto.reduce((acc, c) => acc + (c.integracao_valor_total || c.valor_total || 0), 0);
    const valorRecebidoContratosAberto = contratosAberto.reduce((acc, c) => acc + (c.valor_recebido || 0), 0);

    const totalDespesasMes =
      despesasMes?.reduce(
        (acc, d) => acc + (isDespesaOperacional(d.categoria) ? (d.valor || 0) : 0),
        0
      ) || 0;
    const totalReceitasMes = receitasMes?.reduce((acc, r) => acc + (r.valor || 0), 0) || 0;
    const totalDespesasPagas =
      despesasTotal?.reduce(
        (acc, d) => acc + (isDespesaOperacional(d.categoria) ? (d.valor || 0) : 0),
        0
      ) || 0;
    const totalReceitasRecebidas = receitasTotal?.reduce((acc, r) => acc + (r.valor || 0), 0) || 0;

    return {
      receitas_mes: totalReceitasMes,
      despesas_mes: totalDespesasMes,
      saldo_mes: totalReceitasMes - totalDespesasMes,
      receitas_total: totalReceitasRecebidas,
      despesas_total: totalDespesasPagas,
      saldo_total: totalReceitasRecebidas - totalDespesasPagas,
      contas_pagar_pendentes: contasPagarPendentes?.reduce((acc, c) => acc + (c.valor || 0), 0) || 0,
      contas_receber_pendentes: contasReceberPendentes?.reduce((acc, c) => acc + (c.valor || 0), 0) || 0,
      valor_contratos_aberto: valorContratosAberto,
      total_volume_contratos_aberto: totalVolumeContratosAberto,
      valor_recebido_contratos_aberto: valorRecebidoContratosAberto,
      custos_obras_mes: custosObrasMes?.reduce((acc, c) => acc + (c.valor || 0), 0) || 0,
    };
  }

  async obterDadosPorCategoria(
    tipo: 'receitas' | 'despesas',
    data_inicio: string,
    data_fim: string,
    empresaId?: string | null
  ): Promise<{ name: string; value: number }[]> {
    const tabela = tipo === 'receitas' ? 'contas_receber' : 'contas_pagar';
    const campoData = 'data_vencimento';

    let query = (supabase as any)
      .from(tabela)
      .select('valor, categoria')
      .gte(campoData, data_inicio)
      .lte(campoData, data_fim);

    if (tipo === 'despesas') {
      query = query.neq('status', 'deletada');
    }

    query = comEmpresa(query, empresaId);

    const { data, error } = await query;

    if (error) {
      console.error(`Erro ao obter ${tipo} por categoria:`, error);
      return [];
    }

    const agrupado: { [key: string]: number } = {};
    data?.forEach((item: any) => {
      if (tipo === 'despesas' && !isDespesaOperacional(item.categoria)) return;
      const cat = item.categoria || 'Outros';
      agrupado[cat] = (agrupado[cat] || 0) + (item.valor || 0);
    });

    return Object.entries(agrupado)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }

  // Dados para gráficos
  async obterDadosGrafico(
    periodo: 'mes' | 'trimestre' | 'ano' | 'personalizado' = 'mes',
    dataFocal: Date = new Date(),
    intervalo?: { dataInicio: Date; dataFim: Date },
    empresaId?: string | null
  ): Promise<DadosGrafico[]> {
    let dataInicio: Date;
    let dataFim: Date;

    if (periodo === 'personalizado' && intervalo) {
      dataInicio = startOfMonth(intervalo.dataInicio);
      dataFim = endOfMonth(intervalo.dataFim);
    } else {
      switch (periodo) {
        case 'mes':
          // 2 meses antes até 2 meses depois do mês focal
          dataInicio = startOfMonth(subMonths(dataFocal, 2));
          dataFim = endOfMonth(addMonths(dataFocal, 2));
          break;
        case 'trimestre':
          dataInicio = new Date(dataFocal.getFullYear(), dataFocal.getMonth() - 11, 1);
          dataFim = endOfMonth(dataFocal);
          break;
        case 'ano':
          dataInicio = new Date(dataFocal.getFullYear() - 4, 0, 1);
          dataFim = endOfMonth(new Date(dataFocal.getFullYear(), 11, 1));
          break;
        default:
          dataInicio = startOfMonth(subMonths(dataFocal, 2));
          dataFim = endOfMonth(addMonths(dataFocal, 2));
      }
    }

    // Buscar receitas e despesas agrupadas por mês (por vencimento para bater com as abas)
    const { data: receitas } = await comEmpresa(
      (supabase as any)
        .from('contas_receber')
        .select('valor, data_vencimento')
        .gte('data_vencimento', dataInicio.toISOString().split('T')[0])
        .lte('data_vencimento', dataFim.toISOString().split('T')[0]),
      empresaId
    );

    const { data: despesas } = await comEmpresa(
      (supabase as any)
        .from('contas_pagar')
        .select('valor, data_vencimento, categoria')
        .gte('data_vencimento', dataInicio.toISOString().split('T')[0])
        .lte('data_vencimento', dataFim.toISOString().split('T')[0])
        .neq('status', 'deletada'),
      empresaId
    );

    // Agrupar por mês
    const dadosPorMes: { [key: string]: { receitas: number; despesas: number } } = {};

    // Inicializar todos os meses no intervalo para garantir que apareçam no gráfico mesmo sem dados
    let dataCursor = new Date(dataInicio);
    while (dataCursor <= dataFim) {
      const mesKey = format(dataCursor, 'yyyy-MM');
      dadosPorMes[mesKey] = { receitas: 0, despesas: 0 };
      dataCursor = addMonths(dataCursor, 1);
    }

    receitas?.forEach((r) => {
      if (r.data_vencimento) {
        const mes = r.data_vencimento.substring(0, 7); // YYYY-MM
        if (dadosPorMes[mes]) {
          dadosPorMes[mes].receitas += r.valor || 0;
        }
      }
    });

    despesas?.forEach((d) => {
      if (d.data_vencimento && isDespesaOperacional(d.categoria)) {
        const mes = d.data_vencimento.substring(0, 7); // YYYY-MM
        if (dadosPorMes[mes]) {
          dadosPorMes[mes].despesas += d.valor || 0;
        }
      }
    });

    // Converter para array e ordenar pelas chaves YYYY-MM
    return Object.entries(dadosPorMes)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([mesKey, dados]) => ({
        mes: format(parseISO(`${mesKey}-01`), "MMM 'de' yyyy", { locale: ptBR }),
        receitas: dados.receitas,
        despesas: dados.despesas,
        saldo: dados.receitas - dados.despesas,
      }));
  }
}

export interface ContaRecorrentePagar {
  id: string;
  empresa_id?: string | null;
  descricao: string;
  valor: number;
  categoria: string;
  fornecedor?: string;
  parceiro_id?: string;
  observacoes?: string;
  frequencia: 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual';
  dia_semana?: number; // 0 = domingo, 6 = sábado
  dia_mes?: number; // 1-31
  intervalo_personalizado?: number; // Número de dias (mantido para compatibilidade)
  data_inicio: string;
  data_fim?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface ContaRecorrenteReceber {
  id: string;
  empresa_id?: string | null;
  descricao: string;
  valor: number;
  categoria: string;
  cliente?: string;
  contrato_id?: string;
  observacoes?: string;
  frequencia: 'diaria' | 'semanal' | 'quinzenal' | 'mensal' | 'trimestral' | 'semestral' | 'anual';
  dia_semana?: number; // 0 = domingo, 6 = sábado
  dia_mes?: number; // 1-31
  intervalo_personalizado?: number; // Número de dias (mantido para compatibilidade)
  data_inicio: string;
  data_fim?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

class ContasRecorrentesService {
  private baseUrl = '/api/contas-recorrentes';

  // Contas Recorrentes a Pagar
  async listarContasRecorrentesPagar(empresaId?: string | null): Promise<ContaRecorrentePagar[]> {
    const qs = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    const response = await fetch(`${this.baseUrl}/pagar${qs}`, {
      headers: {
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
    });
    if (!response.ok) throw new Error('Erro ao listar contas recorrentes a pagar');
    return response.json();
  }

  async obterContaRecorrentePagar(id: string): Promise<ContaRecorrentePagar | null> {
    const { data, error } = await supabase
      .from('contas_pagar_recorrentes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erro ao obter conta recorrente a pagar:', error);
      throw error;
    }

    return data as ContaRecorrentePagar;
  }

  async criarContaRecorrentePagar(conta: Omit<ContaRecorrentePagar, 'id' | 'created_at' | 'updated_at'>): Promise<ContaRecorrentePagar> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/pagar`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify(conta),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar conta recorrente a pagar');
    }
    return response.json();
  }

  async atualizarContaRecorrentePagar(id: string, updates: Partial<ContaRecorrentePagar>): Promise<ContaRecorrentePagar> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/pagar/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar conta recorrente a pagar');
    }
    return response.json();
  }

  async deletarContaRecorrentePagar(id: string): Promise<void> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/pagar/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao deletar conta recorrente a pagar');
    }
  }

  async gerarContasPagarRecorrentes(): Promise<{ contas_geradas: number }> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/pagar/gerar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao gerar contas recorrentes a pagar');
    }
    return response.json();
  }

  // Contas Recorrentes a Receber
  async listarContasRecorrentesReceber(empresaId?: string | null): Promise<ContaRecorrenteReceber[]> {
    const session = await supabase.auth.getSession();
    const qs = empresaId ? `?empresa_id=${encodeURIComponent(empresaId)}` : '';
    const response = await fetch(`${this.baseUrl}/receber${qs}`, {
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) throw new Error('Erro ao listar contas recorrentes a receber');
    return response.json();
  }

  async criarContaRecorrenteReceber(conta: Omit<ContaRecorrenteReceber, 'id' | 'created_at' | 'updated_at'>): Promise<ContaRecorrenteReceber> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/receber`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify(conta),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar conta recorrente a receber');
    }
    return response.json();
  }

  async atualizarContaRecorrenteReceber(id: string, updates: Partial<ContaRecorrenteReceber>): Promise<ContaRecorrenteReceber> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/receber/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao atualizar conta recorrente a receber');
    }
    return response.json();
  }

  async deletarContaRecorrenteReceber(id: string): Promise<void> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/receber/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao deletar conta recorrente a receber');
    }
  }

  async obterContaRecorrenteReceber(id: string): Promise<ContaRecorrenteReceber | null> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/receber/${id}`, {
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json();
      throw new Error(error.error || 'Erro ao obter conta recorrente a receber');
    }
    return response.json();
  }

  async gerarContasReceberRecorrentes(): Promise<{ contas_geradas: number }> {
    const session = await supabase.auth.getSession();
    const response = await fetch(`${this.baseUrl}/receber/gerar`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.data.session?.access_token}`,
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao gerar contas recorrentes a receber');
    }
    return response.json();
  }
}

// Função utilitária para calcular recebimentos previstos baseado na modalidade de pagamento
export function calcularRecebimentosPrevistos(contrato: ContratoAberto): RecebimentoPrevisto[] {
  const recebimentos: RecebimentoPrevisto[] = [];
  const modalidade = contrato.integracao_modalidade_pagamento || 1;
  const valorTotal = contrato.integracao_valor_total || contrato.valor_total || 0;

  const formatarFormaPagamento = (forma?: string): string => {
    if (!forma) return 'Não informado';
    const formas: { [key: string]: string } = {
      'pix': 'PIX',
      'boleto': 'Boleto',
      'cartao_credito': 'Cartão de Crédito',
      'cartao_debito': 'Cartão de Débito',
      'financiamento_bancario': 'Financiamento Bancário',
      'transferencia': 'Transferência Bancária'
    };
    return formas[forma] || forma;
  };

  switch (modalidade) {
    case 1: // Pagamento integral na assinatura
      if ((contrato.integracao_valor_assinatura || valorTotal) > 0) {
        recebimentos.push({
          etapa: 'assinatura',
          descricao: 'Assinatura do Contrato',
          valor: contrato.integracao_valor_assinatura || valorTotal,
          percentual: contrato.integracao_percentual_assinatura || 100,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_assinatura),
          numero_parcelas: contrato.integracao_numero_parcelas_assinatura || 1,
          valor_parcela: contrato.integracao_valor_parcela_assinatura || (contrato.integracao_valor_assinatura || valorTotal)
        });
      }
      break;

    case 2: // Assinatura + Conclusão
      if ((contrato.integracao_valor_assinatura || 0) > 0) {
        recebimentos.push({
          etapa: 'assinatura',
          descricao: 'Assinatura do Contrato',
          valor: contrato.integracao_valor_assinatura || 0,
          percentual: contrato.integracao_percentual_assinatura || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_assinatura),
          numero_parcelas: contrato.integracao_numero_parcelas_assinatura || 1,
          valor_parcela: contrato.integracao_valor_parcela_assinatura || contrato.integracao_valor_assinatura || 0
        });
      }
      if ((contrato.integracao_valor_conclusao_obra || 0) > 0) {
        recebimentos.push({
          etapa: 'conclusao',
          descricao: 'Conclusão da Obra',
          valor: contrato.integracao_valor_conclusao_obra || 0,
          percentual: contrato.integracao_percentual_conclusao_obra || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_conclusao_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_conclusao_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_conclusao_obra || contrato.integracao_valor_conclusao_obra || 0
        });
      }
      break;

    case 3: // Assinatura + Início + Conclusão
      if ((contrato.integracao_valor_assinatura || 0) > 0) {
        recebimentos.push({
          etapa: 'assinatura',
          descricao: 'Assinatura do Contrato',
          valor: contrato.integracao_valor_assinatura || 0,
          percentual: contrato.integracao_percentual_assinatura || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_assinatura),
          numero_parcelas: contrato.integracao_numero_parcelas_assinatura || 1,
          valor_parcela: contrato.integracao_valor_parcela_assinatura || contrato.integracao_valor_assinatura || 0
        });
      }
      if ((contrato.integracao_valor_inicio_obra || 0) > 0) {
        recebimentos.push({
          etapa: 'inicio',
          descricao: 'Início da Obra',
          valor: contrato.integracao_valor_inicio_obra || 0,
          percentual: contrato.integracao_percentual_inicio_obra || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_inicio_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_inicio_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_inicio_obra || contrato.integracao_valor_inicio_obra || 0
        });
      }
      if ((contrato.integracao_valor_conclusao_obra || 0) > 0) {
        recebimentos.push({
          etapa: 'conclusao',
          descricao: 'Conclusão da Obra',
          valor: contrato.integracao_valor_conclusao_obra || 0,
          percentual: contrato.integracao_percentual_conclusao_obra || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_conclusao_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_conclusao_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_conclusao_obra || contrato.integracao_valor_conclusao_obra || 0
        });
      }
      break;

    case 4: // Assinatura + Início + Conclusão + Ligação
      if ((contrato.integracao_valor_assinatura || 0) > 0) {
        recebimentos.push({
          etapa: 'assinatura',
          descricao: 'Assinatura do Contrato',
          valor: contrato.integracao_valor_assinatura || 0,
          percentual: contrato.integracao_percentual_assinatura || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_assinatura),
          numero_parcelas: contrato.integracao_numero_parcelas_assinatura || 1,
          valor_parcela: contrato.integracao_valor_parcela_assinatura || contrato.integracao_valor_assinatura || 0
        });
      }
      if ((contrato.integracao_valor_inicio_obra || 0) > 0) {
        recebimentos.push({
          etapa: 'inicio',
          descricao: 'Início da Obra',
          valor: contrato.integracao_valor_inicio_obra || 0,
          percentual: contrato.integracao_percentual_inicio_obra || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_inicio_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_inicio_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_inicio_obra || contrato.integracao_valor_inicio_obra || 0
        });
      }
      if ((contrato.integracao_valor_conclusao_obra || 0) > 0) {
        recebimentos.push({
          etapa: 'conclusao',
          descricao: 'Conclusão da Obra',
          valor: contrato.integracao_valor_conclusao_obra || 0,
          percentual: contrato.integracao_percentual_conclusao_obra || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_conclusao_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_conclusao_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_conclusao_obra || contrato.integracao_valor_conclusao_obra || 0
        });
      }
      if ((contrato.integracao_valor_ligacao_definitiva || 0) > 0) {
        recebimentos.push({
          etapa: 'ligacao',
          descricao: 'Ligação Definitiva',
          valor: contrato.integracao_valor_ligacao_definitiva || 0,
          percentual: contrato.integracao_percentual_ligacao_definitiva || 0,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_ligacao_definitiva),
          numero_parcelas: contrato.integracao_numero_parcelas_ligacao_definitiva || 1,
          valor_parcela: contrato.integracao_valor_parcela_ligacao_definitiva || contrato.integracao_valor_ligacao_definitiva || 0
        });
      }
      break;

    case 5: // Pagamento integral na conclusão da obra
      if ((contrato.integracao_valor_conclusao_obra || valorTotal) > 0) {
        recebimentos.push({
          etapa: 'conclusao',
          descricao: 'Conclusão da Obra',
          valor: contrato.integracao_valor_conclusao_obra || valorTotal,
          percentual: contrato.integracao_percentual_conclusao_obra || 100,
          forma_pagamento: formatarFormaPagamento(contrato.integracao_forma_pagamento_conclusao_obra),
          numero_parcelas: contrato.integracao_numero_parcelas_conclusao_obra || 1,
          valor_parcela: contrato.integracao_valor_parcela_conclusao_obra || (contrato.integracao_valor_conclusao_obra || valorTotal)
        });
      }
      break;
  }

  return recebimentos;
}

/**
 * Calcula o número total de parcelas previstas para um contrato baseado na modalidade
 */
export function calcularTotalParcelasPrevistas(contrato: ContratoAberto): number {
  const modalidade = contrato.integracao_modalidade_pagamento || 1;
  let totalParcelas = 0;

  switch (modalidade) {
    case 1: // Pagamento integral na assinatura
      totalParcelas = contrato.integracao_numero_parcelas_assinatura || 1;
      break;

    case 2: // Assinatura + Conclusão
      totalParcelas = (contrato.integracao_numero_parcelas_assinatura || 1) + 
                      (contrato.integracao_numero_parcelas_conclusao_obra || 1);
      break;

    case 3: // Assinatura + Início + Conclusão
      totalParcelas = (contrato.integracao_numero_parcelas_assinatura || 1) + 
                      (contrato.integracao_numero_parcelas_inicio_obra || 1) + 
                      (contrato.integracao_numero_parcelas_conclusao_obra || 1);
      break;

    case 4: // Assinatura + Início + Conclusão + Ligação
      totalParcelas = (contrato.integracao_numero_parcelas_assinatura || 1) + 
                      (contrato.integracao_numero_parcelas_inicio_obra || 1) + 
                      (contrato.integracao_numero_parcelas_conclusao_obra || 1) + 
                      (contrato.integracao_numero_parcelas_ligacao_definitiva || 1);
      break;

    case 5: // Pagamento integral na conclusão da obra
      totalParcelas = contrato.integracao_numero_parcelas_conclusao_obra || 1;
      break;

    default:
      totalParcelas = 1;
  }

  return totalParcelas;
}

export const contasRecorrentesService = new ContasRecorrentesService();
export const financeiroService = new FinanceiroService();

